import React, { useState } from 'react';
import { apiService } from '../services/api';
import './TicketValidator.css';

const TicketValidator: React.FC = () => {
  const [ticketId, setTicketId] = useState('');
  const [validationResult, setValidationResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleValidate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketId.trim()) return;

    setLoading(true);
    setError('');
    setValidationResult(null);

    try {
      const result = await apiService.validateTicket({ ticketId: ticketId.trim() });
      setValidationResult(result);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to validate ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleUseTicket = async () => {
    if (!validationResult?.ticket?.id) return;

    setLoading(true);
    setError('');

    try {
      const result = await apiService.useTicket(validationResult.ticket.id);
      if (result.success) {
        setValidationResult({
          ...validationResult,
          ticket: {
            ...validationResult.ticket,
            status: 'used'
          },
          message: 'Ticket used successfully!'
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to use ticket');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTicketId('');
    setValidationResult(null);
    setError('');
  };

  return (
    <div className="container">
      <div className="ticket-validator">
        <h1>Ticket Validator</h1>
        <p>Scan or enter ticket ID to validate and use tickets at the event entrance.</p>

        <form onSubmit={handleValidate} className="validation-form">
          <div className="form-group">
            <label htmlFor="ticketId">Ticket ID</label>
            <input
              id="ticketId"
              type="text"
              value={ticketId}
              onChange={(e) => setTicketId(e.target.value)}
              placeholder="Enter or scan ticket ID"
              disabled={loading}
              autoFocus
            />
          </div>
          <div className="form-actions">
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading || !ticketId.trim()}
            >
              {loading ? 'Validating...' : 'Validate Ticket'}
            </button>
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={resetForm}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </form>

        {error && (
          <div className="validation-result error">
            <div className="result-icon">❌</div>
            <div className="result-content">
              <h3>Validation Error</h3>
              <p>{error}</p>
            </div>
          </div>
        )}

        {validationResult && (
          <div className={`validation-result ${validationResult.valid ? 'success' : 'invalid'}`}>
            <div className="result-icon">
              {validationResult.valid ? '✅' : '❌'}
            </div>
            <div className="result-content">
              <h3>
                {validationResult.valid ? 'Valid Ticket' : 'Invalid Ticket'}
              </h3>
              <p>{validationResult.message}</p>

              {validationResult.valid && validationResult.ticket && (
                <div className="ticket-details">
                  <div className="detail-item">
                    <strong>Ticket ID:</strong> {validationResult.ticket.id}
                  </div>
                  <div className="detail-item">
                    <strong>Event:</strong> {validationResult.ticket.eventTitle || validationResult.ticket.eventId}
                  </div>
                  <div className="detail-item">
                    <strong>Attendee:</strong> {validationResult.ticket.userName}
                  </div>
                  <div className="detail-item">
                    <strong>Email:</strong> {validationResult.ticket.userEmail}
                  </div>
                  <div className="detail-item">
                    <strong>Quantity:</strong> {validationResult.ticket.quantity}
                  </div>
                  <div className="detail-item">
                    <strong>Status:</strong> 
                    <span className={`status ${validationResult.ticket.status}`}>
                      {validationResult.ticket.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <strong>Purchase Date:</strong> {new Date(validationResult.ticket.purchaseDate).toLocaleDateString()}
                  </div>
                  {validationResult.ticket.eventDate && (
                    <div className="detail-item">
                      <strong>Event Date:</strong> {new Date(validationResult.ticket.eventDate).toLocaleDateString()}
                    </div>
                  )}
                  {validationResult.ticket.eventLocation && (
                    <div className="detail-item">
                      <strong>Event Location:</strong> {validationResult.ticket.eventLocation}
                    </div>
                  )}

                  {validationResult.ticket.status === 'confirmed' && (
                    <div className="use-ticket-section">
                      <h4>Mark Ticket as Used</h4>
                      <p>Click below to mark this ticket as used for event entry.</p>
                      <button 
                        className="btn btn-success"
                        onClick={handleUseTicket}
                        disabled={loading}
                      >
                        {loading ? 'Processing...' : 'Use Ticket'}
                      </button>
                    </div>
                  )}

                  {validationResult.ticket.status === 'used' && (
                    <div className="ticket-used-notice">
                      <div className="used-icon">🎫</div>
                      <p>This ticket has already been used for entry.</p>
                    </div>
                  )}

                  {validationResult.ticket.status === 'pending' && (
                    <div className="ticket-pending-notice">
                      <div className="pending-icon">⏳</div>
                      <p>This ticket is still pending payment confirmation.</p>
                    </div>
                  )}

                  {validationResult.ticket.status === 'cancelled' && (
                    <div className="ticket-cancelled-notice">
                      <div className="cancelled-icon">🚫</div>
                      <p>This ticket has been cancelled.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="validator-info">
          <h3>How to use the Ticket Validator</h3>
          <ul>
            <li>Enter the ticket ID manually or use a barcode scanner</li>
            <li>Click "Validate Ticket" to check if the ticket is valid</li>
            <li>If valid and confirmed, you can mark it as used for entry</li>
            <li>Used tickets cannot be used again for entry</li>
            <li>Pending tickets need payment confirmation first</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TicketValidator;
